import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { HttpError, queueCard } from '../lib/api.ts';
import { Icon } from './Icon.tsx';

interface AddCardModalProps {
  onClose: () => void;
}

export function AddCardModal({ onClose }: AddCardModalProps) {
  const [url, setUrl] = useState('');
  const [result, setResult] = useState<
    | null
    | { kind: 'queued'; cardId: string; partialMeta: boolean }
    | { kind: 'already-tracked'; cardId: string }
    | { kind: 'error'; msg: string }
  >(null);
  const qc = useQueryClient();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mut = useMutation({
    mutationFn: () => queueCard(url.trim()),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['cards'] });
      if (r.status === 'already-tracked') {
        setResult({ kind: 'already-tracked', cardId: r.cardId });
      } else {
        setResult({
          kind: 'queued',
          cardId: r.cardId,
          partialMeta: r.fetchedMetadata !== true,
        });
      }
    },
    onError: (e) => {
      if (e instanceof HttpError) {
        if (e.status === 400)
          setResult({ kind: 'error', msg: 'That doesn’t look like a Snkrdunk product URL.' });
        else if (e.status === 401)
          setResult({ kind: 'error', msg: 'Session expired — reload and sign in.' });
        else if (e.status === 429)
          setResult({ kind: 'error', msg: 'Daily add limit reached. Try tomorrow.' });
        else setResult({ kind: 'error', msg: `Could not add (${e.status}).` });
      } else setResult({ kind: 'error', msg: 'Network error.' });
    },
  });

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: scrim — Escape closes via global handler */}
      {/* biome-ignore lint/a11y/useSemanticElements: scrim is decorative; close-btn handles keyboard */}
      <div className="scrim" onClick={onClose} role="presentation" />
      <div
        className="trade-modal"
        // biome-ignore lint/a11y/useSemanticElements: matches the existing trade-modal dialog pattern.
        role="dialog"
        aria-label="Add card by URL"
      >
        <div className="trade-head">
          <div>
            <div className="trade-eyebrow mono">Add card to watchlist</div>
            <div className="trade-title">Paste a Snkrdunk URL</div>
            <div className="trade-sub mono">we&rsquo;ll fetch the rest</div>
          </div>
          <button type="button" className="close-btn" aria-label="Close" onClick={onClose}>
            <Icon name="close" size={12} />
          </button>
        </div>
        <div className="trade-body">
          <div className="trade-field">
            <label htmlFor="add-url">Snkrdunk URL</label>
            <input
              id="add-url"
              className="trade-input mono"
              type="url"
              placeholder="https://snkrdunk.com/en/trading-cards/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <div className="trade-hint mono">
              Paste the product page URL. Trading-cards or apparels paths both work.
            </div>
          </div>

          {result?.kind === 'queued' && (
            <div className="trade-pnl pos">
              <div className="trade-pnl-head mono">Queued</div>
              <div className="trade-pnl-meta mono">
                Card {result.cardId} added. Trading data will populate within a few hours.
                {result.partialMeta && ' Name/set will fill in on the next scrape.'}
              </div>
            </div>
          )}
          {result?.kind === 'already-tracked' && (
            <div className="trade-pnl">
              <div className="trade-pnl-head mono">Already tracked</div>
              <div className="trade-pnl-meta mono">
                Card {result.cardId} is already on the dashboard.
              </div>
            </div>
          )}
          {result?.kind === 'error' && <div className="trade-error mono">{result.msg}</div>}
        </div>
        <div className="trade-foot">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            className="btn-primary btn-buy"
            disabled={mut.isPending || url.trim().length === 0}
            onClick={() => {
              setResult(null);
              mut.mutate();
            }}
          >
            {mut.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </>
  );
}
